// frontend/src/pages/Profile.js

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "api";
import AppHeader from "../components/AppHeader";
import ProfileCard from "../components/ProfileCard";
import ActivityCard from "../components/ActivityCard";
import CirclesPanel from "../components/CirclesPanel";
import ConnectionsCard from "../components/ConnectionsCard";
import PlannerQuickPanel from "../components/PlannerQuickPanel";

import MonthlyCalendarModal from "../components/MonthlyCalendarModal";
import WeeklyCalendarModal from "../components/WeeklyCalendarModal";
import WeeklyPlanModal from "../components/WeeklyPlanModal";
import GoalsByMonth from "../components/GoalsByMonth";
import RemindersModal from "../components/RemindersModal";

import { requestConnection } from "../utils/requestConnection";
import { isIOS } from "../utils/platform";
import { computeCalendarMarks } from "../utils/streaks";
import { Capacitor } from "@capacitor/core";

/* ---------- Responsive / native check (match Home) ---------- */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const nativeOrIOS =
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (typeof Capacitor?.isNativePlatform === "function" &&
      Capacitor.isNativePlatform());

  return isMobile || nativeOrIOS;
}

const MAX_READONLY_CIRCLES = 8;

/* ---------- Activity helpers ---------- */

const normalizeTags = (rawActivity) => {
  if (!rawActivity) return [];

  if (Array.isArray(rawActivity.tags)) return rawActivity.tags;

  if (Array.isArray(rawActivity._tags)) return rawActivity._tags;

  if (typeof rawActivity.tags === "string") {
    try {
      const parsed = JSON.parse(rawActivity.tags);
      if (Array.isArray(parsed)) return parsed;
    } catch {}

    return rawActivity.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  if (typeof rawActivity._tags === "string") {
    try {
      const parsed = JSON.parse(rawActivity._tags);
      if (Array.isArray(parsed)) return parsed;
    } catch {}

    return rawActivity._tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  return [];
};

// date -> "YYYY-MM-DD"
const ymd = (d) => {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");

  return `${y}-${m}-${day}`;
};

// group activities by day — used by planner on your own profile
const groupByDay = (list = []) => {
  const map = {};

  list.forEach((a) => {
    const key = ymd(a.timestamp || a.created_at);
    (map[key] ||= []).push(a);
  });

  Object.keys(map).forEach((k) => {
    map[k].sort(
      (a, b) =>
        new Date(a.timestamp || a.created_at) -
        new Date(b.timestamp || b.created_at)
    );
  });

  return map;
};

const Profile = () => {
  const { id } = useParams();
  const profileUserId = Number(id);

  const isMobile = useIsMobile();
  const onIOS = !!isIOS;

  /* ---------- Rail sizing — match Home ---------- */

  const leftRailRef = React.useRef(null);
  const rightRailRef = React.useRef(null);

  const [desktopRailHeight, setDesktopRailHeight] = useState(null);

  /* ---------- State ---------- */

  const [viewer, setViewer] = useState(null);
  const [loadingViewer, setLoadingViewer] = useState(true);

  const [profileUser, setProfileUser] = useState(null);
  const [ownerName, setOwnerName] = useState("");

  const [activities, setActivities] = useState([]);
  const [groups, setGroups] = useState([]);

  const [selectedTag, setSelectedTag] = useState(null);
  const [error, setError] = useState("");

  // "none" | "pending_out" | "pending_in" | "accepted"
  const [connStatus, setConnStatus] = useState("none");

  const [isViewerFollowingProfile, setIsViewerFollowingProfile] =
    useState(false);

  /* ---------- Planner state — only rendered for isSelf ---------- */

  const [showMonthly, setShowMonthly] = useState(false);
  const [showWeekly, setShowWeekly] = useState(false);
  const [showWeekPlan, setShowWeekPlan] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showGoalsByMonth, setGoalsByMonth] = useState(false);

  const isSelf = !!(viewer && viewer.id === profileUserId);

  const canInteract = isSelf || isViewerFollowingProfile;

  const isMobileish = onIOS || isMobile;

  /* ---------- Helpers ---------- */

  const authHeaders = () => {
    const t =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      null;

    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const normalizeUser = (raw, fallbackId) => {
    const r = raw ?? {};

    const name =
      r.display_name ||
      r.name ||
      [r.first_name, r.last_name].filter(Boolean).join(" ") ||
      r.username ||
      (r.id
        ? `User #${r.id}`
        : `User #${fallbackId ?? ""}`.trim());

    const profilePicUrl =
      r.profile_pic_url ||
      r.avatar_url ||
      r.profile_pic ||
      "";

    return {
      ...r,

      id: r.id ?? fallbackId,

      name,

      display_name:
        r.display_name ||
        r.name ||
        name,

      username: r.username || "",
      bio: r.bio || "",

      email: r.email || "",

      profile_pic_url: profilePicUrl,

      avatar_url:
        r.avatar_url ||
        profilePicUrl ||
        null,

      following:
        r.following ??
        r.following_count ??
        0,

      followers:
        r.followers ??
        r.followers_count ??
        0,

      activitiesCount:
        typeof r.activitiesCount === "number"
          ? r.activitiesCount
          : Array.isArray(r.activities)
          ? r.activities.length
          : 0,
    };
  };

  /* ---------- Initial data fetch ---------- */

  useEffect(() => {
    const run = async () => {
      setError("");

      const hdrs = authHeaders();

      /* viewer */
      let meData = null;

      try {
        const me = await api.get("/users/me", {
          headers: hdrs,
        });

        meData = me.data;
        setViewer(me.data);
      } catch {
        setViewer(null);
      } finally {
        setLoadingViewer(false);
      }

      /* profile user */
      let fetchedProfileUser = null;

      try {
        const u = await api.get(
          `/users/${profileUserId}`,
          {
            headers: hdrs,
          }
        );

        fetchedProfileUser = u.data ?? null;

        setProfileUser(fetchedProfileUser);

        const n = normalizeUser(
          fetchedProfileUser,
          profileUserId
        ).name;

        setOwnerName(
          (n || "").trim() ||
            `User #${profileUserId}`
        );
      } catch {
        setProfileUser(null);
        setOwnerName(`User #${profileUserId}`);
      }

      /* activities + groups */
      try {
        const [acts, grps] = await Promise.all([
          api.get(
            `/users/${profileUserId}/activities`,
            {
              headers: hdrs,
            }
          ),

          api.get(
            `/users/${profileUserId}/groups`,
            {
              headers: hdrs,
            }
          ),
        ]);

        const actsArr = Array.isArray(acts.data)
          ? acts.data
          : [];

        const normViewed = normalizeUser(
          fetchedProfileUser,
          profileUserId
        );

        const withUserAndTags = actsArr.map((a) => {
          const user = a?.user
            ? a.user
            : normViewed;

          const tags = normalizeTags(a);

          return {
            ...a,
            user,
            tags,
          };
        });

        // latest -> oldest, matching Home
        withUserAndTags.sort(
          (a, b) =>
            new Date(
              b.timestamp || b.created_at
            ) -
            new Date(
              a.timestamp || a.created_at
            )
        );

        setActivities(withUserAndTags);

        setGroups(
          Array.isArray(grps.data)
            ? grps.data
            : []
        );
      } catch (e) {
        console.error(e);
        setError("Failed to load profile.");
      }

      /* connection status */
      try {
        if (
          !meData ||
          meData.id === profileUserId
        ) {
          setConnStatus("none");
        } else {
          const { data } = await api.get(
            `/connections/status/${profileUserId}`,
            {
              headers: hdrs,
            }
          );

          setConnStatus(
            data?.status || "none"
          );
        }
      } catch {
        setConnStatus("none");
      }
    };

    run();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileUserId]);

  /* ---------- Keep ownerName in sync ---------- */

  useEffect(() => {
    if (profileUser) {
      const n = normalizeUser(
        profileUser,
        profileUserId
      ).name;

      setOwnerName(
        (n || "").trim() ||
          `User #${profileUserId}`
      );
    }
  }, [profileUser, profileUserId]);

  /* ---------- Does viewer follow viewed profile? ---------- */

  useEffect(() => {
    const checkFollowing = async () => {
      try {
        if (
          !viewer ||
          viewer.id === profileUserId
        ) {
          setIsViewerFollowingProfile(false);
          return;
        }

        const { data } = await api.get(
          `/connections/following/${viewer.id}`,
          {
            headers: authHeaders(),
          }
        );

        const arr = Array.isArray(data)
          ? data
          : [];

        setIsViewerFollowingProfile(
          arr.some(
            (u) =>
              u?.id === profileUserId
          )
        );
      } catch {
        setIsViewerFollowingProfile(false);
      }
    };

    checkFollowing();
  }, [viewer, profileUserId]);

  useEffect(() => {
    const updateDesktopRailHeight = () => {
      if (
        onIOS ||
        isMobile ||
        window.innerWidth < 768
      ) {
        setDesktopRailHeight(null);
        return;
      }

      const leftRect =
        leftRailRef.current?.getBoundingClientRect();

      const rightRect =
        rightRailRef.current?.getBoundingClientRect();

      const leftHeight =
        leftRect?.height || 0;

      const rightHeight =
        rightRect?.height || 0;

      /*
      * Both rails begin at the same grid row, so either top value
      * gives us the actual usable viewport height beneath the header.
      *
      * This is better than hard-coding header / margin heights because
      * it automatically adapts if those values change later.
      */
      const railTop =
        leftRect?.top ??
        rightRect?.top ??
        0;

      const availableViewportHeight = Math.max(
        0,
        window.innerHeight - railTop
      );

      /*
      * Desktop middle-column height:
      *
      * 1. At minimum, fill the remaining visible viewport.
      * 2. If either side rail is taller, grow to match the taller rail.
      */
      const targetHeight = Math.ceil(
        Math.max(
          availableViewportHeight,
          leftHeight,
          rightHeight
        )
      );

      if (targetHeight > 0) {
        setDesktopRailHeight(targetHeight);
      }
    };

    const frame = requestAnimationFrame(
      updateDesktopRailHeight
    );

    const observer = new ResizeObserver(
      updateDesktopRailHeight
    );

    if (leftRailRef.current) {
      observer.observe(
        leftRailRef.current
      );
    }

    if (rightRailRef.current) {
      observer.observe(
        rightRailRef.current
      );
    }

    window.addEventListener(
      "resize",
      updateDesktopRailHeight
    );

    return () => {
      cancelAnimationFrame(frame);

      observer.disconnect();

      window.removeEventListener(
        "resize",
        updateDesktopRailHeight
      );
    };
  }, [
    isMobile,
    onIOS,
  ]);

  /* ---------- Filter activities ---------- */

  const filteredActivities = useMemo(() => {
    if (!selectedTag) {
      return activities;
    }

    return activities.filter((a) =>
      (a?.tags || []).includes(
        selectedTag
      )
    );
  }, [activities, selectedTag]);

  /* ---------- Planner data ---------- */

  const activitiesByDay = useMemo(
    () => groupByDay(activities),
    [activities]
  );

  const streakDays = useMemo(
    () =>
      computeCalendarMarks({
        activityDates: Object.keys(
          activitiesByDay || {}
        ),
      }).streakCount,
    [activitiesByDay]
  );

  /* ---------- Connections actions ---------- */

  const refreshStatus = async () => {
    try {
      if (isSelf) return;

      const { data } = await api.get(
        `/connections/status/${profileUserId}`,
        {
          headers: authHeaders(),
        }
      );

      setConnStatus(
        data?.status || "none"
      );
    } catch {
      setConnStatus("none");
    }
  };

  const handleConnect = async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      if (!token) {
        return alert(
          "Please log in to connect."
        );
      }

      const viewed = normalizeUser(
        profileUser,
        profileUserId
      );

      const data = await requestConnection(
        {
          id: viewed.id,

          display_name:
            viewed.display_name ||
            viewed.name ||
            viewed.username,

          avatar_url:
            viewed.avatar_url ||
            viewed.profile_pic_url ||
            null,
        },

        authHeaders
      );

      if (
        data?.status === "accepted"
      ) {
        setConnStatus("accepted");
        setIsViewerFollowingProfile(true);
      } else if (
        data?.status === "pending_out"
      ) {
        setConnStatus("pending_out");
      } else {
        await refreshStatus();
      }
    } catch (e) {
      console.error(e);

      alert(
        e?.response?.data?.detail ||
          "Failed to connect"
      );
    }
  };

  const handleAccept = async () => {
    try {
      await api.put(
        `/connections/accept/${profileUserId}`,
        null,
        {
          headers: authHeaders(),
        }
      );

      setConnStatus("accepted");
      setIsViewerFollowingProfile(true);
    } catch (e) {
      console.error(e);

      alert(
        e?.response?.data?.detail ||
          "Failed to accept"
      );
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete(
        `/connections/${profileUserId}`,
        {
          headers: authHeaders(),
        }
      );

      setConnStatus("none");
      setIsViewerFollowingProfile(false);
    } catch (e) {
      console.error(e);

      alert(
        e?.response?.data?.detail ||
          "Failed to remove"
      );
    }
  };

  /* ---------- Loading / errors ---------- */

  if (error) {
    return (
      <div className="p-8 text-red-600">
        {error}
      </div>
    );
  }

  if (
    loadingViewer &&
    !profileUser &&
    !activities.length &&
    !groups.length
  ) {
    return (
      <div className="p-8">
        Loading…
      </div>
    );
  }

  /* ---------- User displayed in ProfileCard ---------- */

  const u = normalizeUser(
    profileUser ??
      activities?.[0]?.user ??
      {},
    profileUserId
  );

  const userForCard = {
    ...u,

    bio:
      profileUser?.bio ??
      u.bio ??
      "",

    username:
      profileUser?.username ??
      u.username ??
      "",

    name:
      profileUser?.name ??
      u.name,

    display_name:
      profileUser?.display_name ??
      profileUser?.name ??
      u.display_name ??
      u.name,

    profile_pic_url:
      profileUser?.profile_pic_url ??
      u.profile_pic_url,

    followers:
      profileUser?.followers ??
      u.followers ??
      0,

    following:
      profileUser?.following ??
      u.following ??
      0,

    activities,

    activitiesCount:
      activities.length,
  };

  const latestActivity =
    activities?.[0] || null;

  const container =
    "max-w-7xl mx-auto px-6 md:px-10 xl:px-16";

  return (
    <div className="app-shell bg-blue-50">
      <header className="app-header">
        <AppHeader user={viewer} />
      </header>

      <main
        className={`app-content w-full ${container} pt-[calc(64px+var(--safe-top))] md:mt-4 md:pb-14 px-0 py-8`}
      >
        <div
          className="
            grid grid-cols-1
            md:grid-cols-[1fr_minmax(0,2fr)_1fr]
            gap-10
            md:overflow-visible
          "
          style={{ height: "auto" }}
        >
          {/* ======================================================
              LEFT
              Profile card always.
              Planner ONLY when viewing your own profile.
             ====================================================== */}

          <div className="-mb-8 md:self-start">
            <div
              ref={leftRailRef}
              className="md:sticky top-0 md:h-max"
              style={{
                height: "max-content",
              }}
            >
              <ProfileCard
                user={userForCard}
                viewer={viewer}
                canEdit={isSelf}
                readOnly={!canInteract}
                onUserUpdate={
                  isSelf
                    ? (updatedUser) => {
                        setProfileUser(
                          (previous) => ({
                            ...(previous || {}),
                            ...updatedUser,
                          })
                        );

                        setViewer(
                          (previous) =>
                            previous
                              ? {
                                  ...previous,
                                  ...updatedUser,
                                }
                              : previous
                        );
                      }
                    : undefined
                }
                onActivitiesClick={() => {}}
                latestActivity={
                  latestActivity
                }
                isSelf={isSelf}
                connStatus={
                  connStatus
                }
                onConnect={
                  handleConnect
                }
                onAccept={
                  handleAccept
                }
                onRemove={
                  handleRemove
                }
              />

              {/* Planner only appears on YOUR profile */}
              {isSelf &&
                !isMobileish && (
                  <div className="hidden md:block mt-4">
                    <PlannerQuickPanel
                      keySuffix="profile"
                      monthLabel={new Date()
                        .toLocaleString(
                          "en-US",
                          {
                            month:
                              "short",
                          }
                        )
                        .toUpperCase()}
                      onMonthly={() =>
                        setShowMonthly(
                          true
                        )
                      }
                      onWeekly={() =>
                        setShowWeekly(
                          true
                        )
                      }
                      onWeekPlan={() =>
                        setShowWeekPlan(
                          true
                        )
                      }
                      onGoals={() =>
                        setGoalsByMonth(
                          true
                        )
                      }
                      onReminders={() =>
                        setShowReminders(
                          true
                        )
                      }
                      streakDays={
                        streakDays
                      }
                      activitiesByDay={
                        activitiesByDay
                      }
                    />
                  </div>
                )}
            </div>
          </div>

          {/* ======================================================
              MIDDLE
              Height matches taller side rail.
              Only activity feed scrolls on desktop.
             ====================================================== */}

          <section
            className={`md:col-span-1 flex flex-col min-h-0 ${
              selectedTag
                ? "space-y-6"
                : "space-y-4"
            } ${
              isMobileish
                ? "overflow-visible"
                : "overflow-hidden"
            } ${
              isMobileish &&
              selectedTag
                ? "-mt-4"
                : ""
            }`}
            style={
              isMobileish
                ? undefined
                : {
                    height:
                      desktopRailHeight !=
                      null
                        ? `${desktopRailHeight}px`
                        : undefined,
                  }
            }
          >
            {/* Profile has no FeedScopeSelect */}

            <section className="flex min-h-0 flex-1 flex-col">
              {selectedTag && (
                <div className="ml-6 mb-3 -mt-4 flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Filtering by tag:
                  </span>

                  <span className="!min-h-0 !min-w-0 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-700">
                    #{selectedTag}
                  </span>

                  <button
                    onClick={() =>
                      setSelectedTag(
                        null
                      )
                    }
                    className="!min-h-0 !min-w-0 py-1 text-sm text-gray-500 hover:font-semibold"
                  >
                    clear
                  </button>
                </div>
              )}

              {/* Desktop: ONLY this feed area scrolls */}
              <div
                className={`relative z-0 min-h-0 flex-1 space-y-4 ${
                  isMobileish
                    ? "overflow-visible -mt-4"
                    : "overflow-y-auto overscroll-y-contain no-scrollbar pb-28"
                }`}
              >
                {activities.length ===
                0 ? (
                  <div className="flex min-h-[280px] items-start mt-[60px] sm:items-center sm:mt-0 justify-center px-6 text-center">
                    <p className="text-sm text-gray-500">
                      No activities yet
                    </p>
                  </div>
                ) : (
                  (
                    selectedTag
                      ? filteredActivities
                      : activities
                  ).map(
                    (
                      activity,
                      idx
                    ) => {
                      const withUser =
                        activity?.user
                          ? activity
                          : {
                              ...activity,
                              user: u,
                            };

                      return (
                        <ActivityCard
                          key={
                            withUser.id ||
                            `a-${idx}`
                          }
                          activity={
                            withUser
                          }
                          currentUser={
                            viewer
                          }
                          onTagClick={
                            setSelectedTag
                          }
                        />
                      );
                    }
                  )
                )}
              </div>
            </section>
          </section>

          {/* ======================================================
              RIGHT
              Always belongs to VIEWED profile.
              Rachel -> Rachel's circles/connections.
              You -> your circles/connections.
             ====================================================== */}

          {!isMobileish && (
            <aside className="hidden md:block self-stretch">
              <div
                ref={rightRailRef}
                className="sticky md:z-0"
                style={{
                  top: "calc(env(safe-area-inset-top) + 0px)",
                  height:
                    "max-content",
                }}
              >
                <CirclesPanel
                  joinedCircles={
                    groups
                  }
                  readOnly={
                    !canInteract
                  }
                  self={isSelf}
                  ownerName={
                    ownerName
                  }
                  maxIcons={
                    MAX_READONLY_CIRCLES
                  }
                />

                <div className="mt-4">
                  <ConnectionsCard
                    meId={
                      profileUserId
                    }
                    ownerName={
                      ownerName
                    }
                    viewerId={
                      viewer?.id
                    }
                  />
                </div>
              </div>
            </aside>
          )}
        </div>

        <div className="app-footer-pad" />
      </main>

      {/* ==========================================================
          Planner modals
          These can only be opened because PlannerQuickPanel itself
          is only rendered when isSelf.
         ========================================================== */}

      {isSelf && (
        <>
          <MonthlyCalendarModal
            open={showMonthly}
            onClose={() =>
              setShowMonthly(false)
            }
            activitiesByDay={
              activitiesByDay
            }
            currentUser={viewer}
            streakDays={
              streakDays
            }
          />

          <WeeklyCalendarModal
            open={showWeekly}
            onClose={() =>
              setShowWeekly(false)
            }
            activitiesByDay={
              activitiesByDay
            }
            currentUser={viewer}
          />

          <WeeklyPlanModal
            open={showWeekPlan}
            onClose={() =>
              setShowWeekPlan(false)
            }
            keySuffix="profile"
          />

          <GoalsByMonth
            open={
              showGoalsByMonth
            }
            onClose={() =>
              setGoalsByMonth(false)
            }
            keySuffix="profile"
          />

          <RemindersModal
            open={showReminders}
            onClose={() =>
              setShowReminders(false)
            }
            storageKey="profile:reminders"
          />
        </>
      )}
    </div>
  );
};

export default Profile;